<template>
    <div class="form-manager">
        <!-- Form Builder (create/edit mode) -->
        <FormBuilder
            v-if="mode !== 'list'"
            :edit-form-id="editFormId"
            @back="mode = 'list'; editFormId = null; loadForms()"
            @saved="mode = 'list'; editFormId = null; loadForms()"
        />

        <!-- Form list mode -->
        <div v-else>
            <div class="row items-center q-mb-md">
                <q-space />
                <q-btn
                    unelevated
                    no-caps
                    color="primary"
                    icon="add"
                    label="New Form"
                    @click="mode = 'create'; editFormId = null"
                />
            </div>

            <q-banner v-if="versionError" class="bg-warning q-mb-md">{{ versionError }}</q-banner>
            <q-inner-loading :showing="loading" label="Loading forms..." />

            <q-table
                v-if="!loading"
                :rows="forms"
                :columns="columns"
                row-key="id"
                flat
                bordered
                :pagination="{ rowsPerPage: 0 }"
                hide-pagination
                class="client-table"
            >
                <template v-slot:body-cell-actions="props">
                    <q-td :props="props">
                        <q-btn v-if="props.row.versionStatus === 'draft'" flat dense no-caps color="primary" icon="edit" label="Edit" @click="startEdit(props.row.id)" />
                        <q-btn v-if="props.row.versionStatus === 'draft'" flat dense no-caps color="negative" icon="delete" label="Delete" @click="confirmDelete(props.row)" />
                        <q-btn v-if="props.row.versionStatus !== 'draft'" flat dense no-caps label="New version" @click="changeVersion(props.row, 'replacement')" />
                        <q-btn v-if="props.row.versionStatus === 'draft'" flat dense no-caps color="positive" label="Publish" @click="changeVersion(props.row, 'publish')" />
                        <q-btn v-if="props.row.versionStatus === 'current'" flat dense no-caps label="Retire" @click="changeVersion(props.row, 'retire')" />
                    </q-td>
                </template>
            </q-table>

            <div v-if="!loading && forms.length === 0" class="text-grey-6 text-center q-pa-xl">
                No forms defined yet. Click "New Form" to create one.
            </div>
        </div>

        <!-- Delete confirmation dialog -->
        <q-dialog v-model="showDeleteDialog">
            <q-card>
                <q-card-section>
                    <div class="text-h6">Delete Form</div>
                    <div class="q-mt-sm">
                        Are you sure you want to delete "{{ formToDelete?.title }}"?
                        This cannot be undone and will fail if responses exist.
                    </div>
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn flat label="Cancel" v-close-popup />
                    <q-btn flat color="negative" label="Delete" :loading="deleting" @click="deleteForm" />
                </q-card-actions>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import formsApi from "@/services/api/forms";
import FormBuilder from "./FormBuilder.vue";

export default {
    name: "FormManager",
    components: { FormBuilder },
    data() {
        return {
            versionError: '',
            mode: 'list', // 'list' | 'create' | 'edit'
            loading: false,
            deleting: false,
            forms: [],
            editFormId: null,
            showDeleteDialog: false,
            formToDelete: null,
            columns: [
                { name: 'id', label: 'ID', field: 'id', align: 'left', sortable: true },
                { name: 'title', label: 'Title', field: 'title', align: 'left', sortable: true },
                { name: 'description', label: 'Description', field: 'description', align: 'left' },
                { name: 'versionNumber', label: 'Version', field: 'versionNumber', align: 'left' },
                { name: 'versionStatus', label: 'Status', field: 'versionStatus', align: 'left' },
                { name: 'actions', label: 'Actions', field: 'actions', align: 'right' },
            ],
        };
    },
    async mounted() {
        await this.loadForms();
    },
    methods: {
        async changeVersion(form, action) {
            this.versionError = '';
            try {
                const result = await formsApi.changeFormVersion(form.id, action);
                await this.loadForms();
                if (action === 'replacement') this.startEdit(result.data.id);
            } catch (error) { this.versionError = error.response?.data?.message || 'Unable to change form version.'; }
        },
        async loadForms() {
            this.loading = true;
            try {
                const response = await formsApi.getDynamicFormTypes();
                const data = response.data?.data || response.data;
                this.forms = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error("Failed to load forms:", error);
            } finally {
                this.loading = false;
            }
        },
        startEdit(formId) {
            this.editFormId = formId;
            this.mode = 'edit';
        },
        confirmDelete(form) {
            this.formToDelete = form;
            this.showDeleteDialog = true;
        },
        async deleteForm() {
            this.deleting = true;
            try {
                await formsApi.deleteFormDefinition(this.formToDelete.id);
                this.showDeleteDialog = false;
                this.formToDelete = null;
                await this.loadForms();
            } catch (error) {
                console.error("Failed to delete form:", error);
            } finally {
                this.deleting = false;
            }
        },
    },
};
</script>

<style scoped>
.form-manager {
    margin: 0 auto;
}
</style>
